// src/pages/budgeting/towerbudgeting/TowerBudgetingCMEForm.jsx
// ✅ FIXED - Correct BOQ data structure

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Edit2, Trash2, X, Save, Package, ChevronDown, ChevronRight, Info, AlertCircle, FileText, FileDown, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';
import ItemPickerModal from '../../../components/boq/ItemPickerModal';
import { getBOQsByTowerSiteIdAndType } from '../../../services/towerSiteBOQService';
import { 
  getRAPsByTowerSiteIdAndType,
  saveRAP,
  approveRAP,
  unapproveRAP 
} from '../../../services/towerSiteRAPService';
import budgetCategoryService from '../../../services/budgetCategoryService';
import { getAllItems } from '../../../services/itemService';
import TowerBudgetingCMESummaryModal from '../../../components/budgeting/TowerBudgetingCMESummaryModal';
import { exportRAPDetailToPDF, exportRAPDetailToExcel } from '../../../utils/rapDetailExport';

// ✅ NEW: TR CODE Master Data
const TR_CODE_OPTIONS = [
  { value: 'TR-0001', label: 'TR-0001 - Upah Sipil' },
  { value: 'TR-0002', label: 'TR-0002 - Material Alam' },
  { value: 'TR-0003', label: 'TR-0003 - Besi Beton' },
  { value: 'TR-0004', label: 'TR-0004 - TOWER SST 72 M' },
  { value: 'TR-0005', label: 'TR-0005 - ME' },
  { value: 'TR-0006', label: 'TR-0006 - Install PLN' },
  { value: 'TR-0007', label: 'TR-0007 - Pengiriman Material ke Site' },
  { value: 'TR-0008', label: 'TR-0008 - Upah ME' },
  { value: 'TR-0009', label: 'TR-0009 - OPS SPV' },
  { value: 'TR-0010', label: 'TR-0010 - Bongkar Langsir Besi Beton' },
  { value: 'TR-0011', label: 'TR-0011 - Bongkar Langsir ME dan Tower' },
  { value: 'TR-0012', label: 'TR-0012 - Borpile' },
  { value: 'TR-0013', label: 'TR-0013 - OPS Pengecoran' },
  { value: 'TR-0014', label: 'TR-0014 - Upah Erection' },
  { value: 'TR-0015', label: 'TR-0015 - Keamanan' },
  { value: 'TR-0016', label: 'TR-0016 - Additional' }
];

// ✅ NEW: POSTING TYPE Master Data
const POSTING_OPTIONS = [
  { value: 'BOS', label: 'BOS' },
  { value: 'BHO', label: 'BHO' }
];

/**
 * Tower Budgeting CME Form - WITH HIERARCHICAL TREE STRUCTURE
 * @param {string} props.mode - 'edit' or 'view' (default: 'edit')
 */
const TowerBudgetingCMEForm = ({ 
  tower, 
  mode = 'edit',  // ✅ NEW: Add mode prop
  onBack, 
  onSave 
}) => {
  // ============= STATE MANAGEMENT =============
  
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [rapData, setRAPData] = useState({});
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [priceCoefficient, setPriceCoefficient] = useState(1.0);
  const [viewMode] = useState(mode);  // ✅ NEW
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  // ============= LIFECYCLE =============
  
  useEffect(() => {
    console.log('🔍 TOWER DATA:', tower);
    
    const coefficientValue = tower.priceCoefficient || tower.price_coefficient || 1.0;
    console.log('✅ COEFFICIENT:', coefficientValue);
    setPriceCoefficient(coefficientValue);
    
    loadCategories();
    loadBOQAndRAP();
  }, [tower.id]);

  // ============= DATA LOADING =============
  
  const loadCategories = async () => {
    setIsLoadingCategories(true);
    try {
      console.log('📂 Loading CME budget categories tree...');
      
      const categoryTree = await budgetCategoryService.getRootCategoriesTree('TOWER_CME');
      
      console.log('✅ Received category tree:', categoryTree);
      
      if (!categoryTree || categoryTree.length === 0) {
        console.warn('⚠️ No CME categories found!');
        toast.error('No CME budget categories available');
        return;
      }
      
      setCategories(categoryTree);
      
      // Auto-expand all categories
      const allCategoryIds = new Set();
      const collectIds = (cats) => {
        cats.forEach(cat => {
          allCategoryIds.add(cat.id);
          if (cat.children && cat.children.length > 0) {
            collectIds(cat.children);
          }
        });
      };
      collectIds(categoryTree);
      setExpandedCategories(allCategoryIds);
      
      console.log('✅ Loaded CME category tree with', categoryTree.length, 'root categories');
    } catch (error) {
      console.error('❌ Error loading categories:', error);
      toast.error('Failed to load budget categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };
  
  /**
   * ✅ FIXED: Load BOQ and RAP data with CORRECT structure
   */
  /**
   * Load BOQ and RAP data with TR CODE & POSTING TYPE support
   * ✅ Handles existing RAP (loads trCode & postingType from backend)
   * ✅ Handles new RAP from BOQ (initializes trCode & postingType as null)
   */
  const loadBOQAndRAP = async () => {
    setIsLoading(true);
    try {
      console.log('📂 Loading BOQ and RAP data...');
      
      // Try to load existing RAPs first
      const raps = await getRAPsByTowerSiteIdAndType(tower.id, 'CME');
      
      if (raps && raps.length > 0) {
        // ============= SCENARIO A: EXISTING RAP =============
        // RAP exists - use RAP data (includes TR CODE and POSTING TYPE from backend)
        console.log('✅ Found existing RAPs:', raps);
        const transformed = {};
        
        raps.forEach(rap => {
          transformed[rap.budgetCategoryId] = {
            rapId: rap.id,
            items: rap.items.map(item => ({
              ...item,  // ✅ Spreads ALL fields including trCode and postingType from backend
              tempId: `rap-${item.id}`,
            })),
            boqSubtotal: rap.boqSubtotalAmount || 0,
            rapSubtotal: rap.rapSubtotalAmount || 0,
            remarks: rap.remarks || '',
            isApproved: rap.isApproved || false,
            approvedBy: rap.approvedBy,
            approvedAt: rap.approvedAt
          };
        });
        
        setRAPData(transformed);
        console.log('✅ Loaded RAP data:', transformed);
        
      } else {
        // ============= SCENARIO B: NEW RAP FROM BOQ =============
        // No RAP - try to load BOQ and convert to RAP format
        console.log('📂 No RAP found, loading BOQ data...');
        const boqs = await getBOQsByTowerSiteIdAndType(tower.id, 'CME');
        
        if (boqs && boqs.length > 0) {
          console.log('✅ Found BOQs, converting to RAP format:', boqs);
          const transformed = {};
          
          boqs.forEach(boq => {
            // ✅ Convert BOQ items to RAP format
            const rapItems = (boq.items || []).map((boqItem, index) => ({
              tempId: `boq-${boqItem.id}`,
              sourceType: 'BOQ',
              boqItemId: boqItem.id,
              itemId: boqItem.itemId,
              itemNumber: boqItem.itemNumber,
              itemCode: boqItem.itemCode,
              itemDescription: boqItem.itemDescription,
              itemType: boqItem.itemType,
              unit: boqItem.unit,
              volume: boqItem.volume,
              // BOQ prices (calculated on-the-fly in BOQ service)
              boqUnitPrice: boqItem.finalUnitPrice || 0,
              boqTotalPrice: boqItem.totalPrice || 0,
              // RAP prices (initially same as BOQ)
              rapUnitPrice: boqItem.finalUnitPrice || 0,
              rapTotalPrice: boqItem.totalPrice || 0,
              // RAP controls
              isNotUsed: false,
              isOverride: false,
              overriddenRapUnitPrice: null,
              remarks: boqItem.remarks || '',
              sequenceOrder: index + 1,
              canDelete: true,
              // ✅ NEW: Initialize TR CODE and POSTING TYPE as null
              // User will select these values in the form
              trCode: null,
              postingType: null
            }));
            
            transformed[boq.budgetCategoryId] = {
              items: rapItems,
              boqSubtotal: boq.subtotalAmount || 0,
              rapSubtotal: boq.subtotalAmount || 0,
              remarks: boq.remarks || ''
            };
          });
          
          setRAPData(transformed);
          console.log('✅ Converted BOQ to RAP format:', transformed);
        } else {
          console.warn('⚠️ No BOQ found for CME');
          toast.error('No CME BOQ found. Please create CME BOQ first.');
        }
      }
      
    } catch (error) {
      console.error('❌ Error loading BOQ/RAP:', error);
      toast.error('Failed to load BOQ/RAP data');
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // EXPLANATION:
  // ============================================================================
  //
  // WHEN LOADING EXISTING RAP:
  // - Backend returns items with trCode and postingType already set
  // - We use {...item} to spread ALL fields including the new ones
  // - Dropdowns will show the selected values
  //
  // WHEN LOADING FROM BOQ (NEW RAP):
  // - BOQ doesn't have trCode or postingType
  // - We initialize them as null: trCode: null, postingType: null
  // - Dropdowns will show "Pilih TR CODE" / "Pilih POSTING"
  // - Amber background highlights empty fields
  //
  // ============================================================================

  // ============= UI HANDLERS =============
  
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };
  
  const handleAddItem = (categoryId) => {
    setCurrentCategoryId(categoryId);
    setShowItemPicker(true);
  };
  
  const handleItemSelect = async (item) => {
    if (!currentCategoryId) return;
    
    try {
      const basePrice = item.defaultPrice || 0;
      const rapUnitPrice = basePrice * priceCoefficient;
      
      const newItem = {
        tempId: `new-${Date.now()}`,
        sourceType: 'NEW',
        boqItemId: null,
        itemId: item.id,
        itemNumber: item.itemNumber,
        itemCode: item.itemCode,
        itemDescription: item.itemName,
        itemType: item.itemType,
        unit: item.unit,
        volume: 1,
        boqUnitPrice: null,
        boqTotalPrice: null,
        isNotUsed: false,
        isOverride: false,
        overriddenRapUnitPrice: null,
        rapUnitPrice: rapUnitPrice,
        rapTotalPrice: rapUnitPrice * 1,
        priceCoefficient: priceCoefficient,
        sequenceOrder: (rapData[currentCategoryId]?.items?.length || 0) + 1,
        remarks: '',
        canDelete: true,
        isFromBOQ: false
      };
      
      setRAPData(prev => ({
        ...prev,
        [currentCategoryId]: {
          ...prev[currentCategoryId],
          items: [...(prev[currentCategoryId]?.items || []), newItem]
        }
      }));
      
      setShowItemPicker(false);
      toast.success('Item added successfully');
      
    } catch (error) {
      console.error('❌ Error adding item:', error);
      toast.error('Failed to add item');
    }
  };
  
  const handleEditItem = (categoryId, item) => {
    setEditingItem({ categoryId, item });
    setEditFormData({
      volume: item.volume.toString(),
      isNotUsed: item.isNotUsed,
      isOverride: item.isOverride,
      overriddenRapUnitPrice: item.overriddenRapUnitPrice?.toString() || '',
      remarks: item.remarks || '',
      // ✅ NEW: Include TR CODE and POSTING TYPE in edit form
      trCode: item.trCode || '',
      postingType: item.postingType || ''      

    });
    setShowEditModal(true);
  };
  
  const handleDeleteItem = (categoryId, tempId) => {
    if (!window.confirm('Delete this item?')) return;
    
    setRAPData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.filter(item => item.tempId !== tempId);
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
    
    toast.success('Item deleted');
  };
  
  const handleCheckboxChange = (categoryId, tempId, field, value) => {
    setRAPData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.map(item => {
        if (item.tempId === tempId) {
          const updated = { ...item, [field]: value };
          
          if (field === 'isNotUsed' && value) {
            updated.isOverride = false;
            updated.rapUnitPrice = 0;
            updated.rapTotalPrice = 0;
          } else if (field === 'isOverride' && value) {
            updated.isNotUsed = false;
          } else if (field === 'isNotUsed' && !value) {
            const basePrice = updated.boqUnitPrice || (item.itemId ? 0 : updated.rapUnitPrice);
            updated.rapUnitPrice = basePrice;
            updated.rapTotalPrice = basePrice * updated.volume;
          }
          
          return updated;
        }
        return item;
      });
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
  };
  
  const handleInlineOverridePrice = (categoryId, tempId, newPrice) => {
    setRAPData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.map(item => {
        if (item.tempId === tempId) {
          const updated = { ...item };
          updated.overriddenRapUnitPrice = newPrice;
          updated.rapUnitPrice = newPrice;
          updated.rapTotalPrice = newPrice * updated.volume;
          return updated;
        }
        return item;
      });
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
  };
  
  const handleSaveEdit = () => {
    if (!editingItem) return;
    
    const { categoryId, item } = editingItem;
    const volume = parseFloat(editFormData.volume) || 0;
    const overriddenPrice = editFormData.isOverride 
      ? (parseFloat(editFormData.overriddenRapUnitPrice) || 0)
      : null;
    
    setRAPData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.map(i => {
        if (i.tempId === item.tempId) {
          const updated = {
            ...i,
            volume,
            isNotUsed: editFormData.isNotUsed,
            isOverride: editFormData.isOverride,
            overriddenRapUnitPrice: overriddenPrice,
            remarks: editFormData.remarks,
            // ✅ NEW: Update TR CODE and POSTING TYPE from edit form
            trCode: editFormData.trCode || null,
            postingType: editFormData.postingType || null            
          };
          
          if (editFormData.isNotUsed) {
            updated.rapUnitPrice = 0;
            updated.rapTotalPrice = 0;
          } else if (editFormData.isOverride && overriddenPrice) {
            updated.rapUnitPrice = overriddenPrice;
            updated.rapTotalPrice = overriddenPrice * volume;
          } else {
            const basePrice = updated.boqUnitPrice || updated.rapUnitPrice;
            updated.rapTotalPrice = basePrice * volume;
          }
          
          return updated;
        }
        return i;
      });
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
    
    setShowEditModal(false);
    setEditingItem(null);
    toast.success('Item updated');
  };
  
  const handleSave = async () => {
    // ✅ NEW: Validate before save
    if (!validateBeforeSave()) return;
    if (!window.confirm('Save RAP CME for this tower?')) return;
    
    setIsSaving(true);
    
    try {
      // Collect all leaf categories recursively
      const leafCategories = [];
      const collectLeafCategories = (cats) => {
        cats.forEach(cat => {
          if (cat.canAddDirect) {
            leafCategories.push(cat);
          }
          if (cat.children && cat.children.length > 0) {
            collectLeafCategories(cat.children);
          }
        });
      };
      collectLeafCategories(categories);
      
      // Save each category that has items
      const savePromises = leafCategories
        .filter(category => rapData[category.id]?.items?.length > 0)
        .map(category => {
          const categoryData = rapData[category.id];
          
          const request = {
            towerSiteId: tower.id,
            budgetCategoryId: category.id,
            rapType: 'CME',
            remarks: categoryData.remarks || '',
            items: categoryData.items.map(item => ({
              sourceType: item.sourceType,
              itemId: item.itemId,
              boqItemId: item.boqItemId,
              volume: parseFloat(item.volume) || 0,
              boqUnitPrice: item.boqUnitPrice || null,
              boqTotalPrice: item.boqTotalPrice || null,
              isNotUsed: item.isNotUsed || false,
              isOverride: item.isOverride || false,
              overriddenRapUnitPrice: item.isOverride && item.overriddenRapUnitPrice 
                ? parseFloat(item.overriddenRapUnitPrice) 
                : null,
              sequenceOrder: item.sequenceOrder || 0,
              remarks: item.remarks || '',
              // ✅ NEW: Include TR CODE and POSTING TYPE in save request
              trCode: item.trCode || null,
              postingType: item.postingType || null
            }))
          };
          
          return saveRAP(request);
        });
      
      await Promise.all(savePromises);
      
      toast.success('RAP saved successfully!');
      
      if (onSave) {
        onSave(tower.id);
      }
      
    } catch (error) {
      console.error('❌ Error saving RAP:', error);
      toast.error('Failed to save RAP');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleApprove = async (categoryId) => {
    const categoryData = rapData[categoryId];
    if (!categoryData || !categoryData.rapId) {
      toast.error('Please save RAP first');
      return;
    }
    
    if (!window.confirm('Approve this RAP?')) return;
    
    try {
      await approveRAP(categoryData.rapId, 'Admin');
      toast.success('RAP approved');
      loadBOQAndRAP();
    } catch (error) {
      console.error('❌ Error approving RAP:', error);
      toast.error('Failed to approve RAP');
    }
  };
  
  const handleUnapprove = async (categoryId) => {
    const categoryData = rapData[categoryId];
    if (!categoryData || !categoryData.rapId) return;
    
    if (!window.confirm('Unapprove this RAP?')) return;
    
    try {
      await unapproveRAP(categoryData.rapId);
      toast.success('RAP unapproved');
      loadBOQAndRAP();
    } catch (error) {
      console.error('❌ Error unapproving RAP:', error);
      toast.error('Failed to unapprove RAP');
    }
  };

  // ✅ NEW: Handle TR CODE change
  const handleTRCodeChange = (categoryId, tempId, trCode) => {
    setRAPData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.map(item => 
        item.tempId === tempId ? { ...item, trCode } : item
      );
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
  };

  // ✅ NEW: Handle POSTING TYPE change
  const handlePostingChange = (categoryId, tempId, postingType) => {
    setRAPData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.map(item => 
        item.tempId === tempId ? { ...item, postingType } : item
      );
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
  };

  const handleExportDetailPDF = () => {
    exportRAPDetailToPDF(tower, rapData, categories, 'CME', priceCoefficient);
  };

  const handleExportDetailExcel = () => {
    exportRAPDetailToExcel(tower, rapData, categories, 'CME', priceCoefficient);
  };

  // ✅ NEW: Validate TR CODE and POSTING TYPE before save
  const validateBeforeSave = () => {
    // Collect all leaf categories
    const leafCategories = [];
    const collectLeafCategories = (cats) => {
      cats.forEach(cat => {
        if (cat.canAddDirect) {
          leafCategories.push(cat);
        }
        if (cat.children && cat.children.length > 0) {
          collectLeafCategories(cat.children);
        }
      });
    };
    collectLeafCategories(categories);
    
    // Find items without TR CODE or POSTING TYPE
    const emptyItems = [];
    
    leafCategories.forEach(category => {
      const items = rapData[category.id]?.items || [];
      items.forEach((item, idx) => {
        if (!item.trCode || !item.postingType) {
          emptyItems.push({
            category: category.categoryCode,
            item: item.itemDescription,
            index: idx + 1,
            missingFields: [
              !item.trCode ? 'TR CODE' : null,
              !item.postingType ? 'POSTING' : null
            ].filter(Boolean)
          });
        }
      });
    });
    
    if (emptyItems.length > 0) {
      const itemList = emptyItems.slice(0, 5).map(item => 
        `• ${item.category} - ${item.item} (Missing: ${item.missingFields.join(', ')})`
      ).join('\n');
      
      const moreText = emptyItems.length > 5 ? `\n... and ${emptyItems.length - 5} more items` : '';
      
      const message = `⚠️ WARNING: Found ${emptyItems.length} items without TR CODE or POSTING:\n\n${itemList}${moreText}\n\nItems without TR CODE will NOT be included in RAP Summary.\n\nDo you want to save anyway?`;
      
      return window.confirm(message);
    }
    
    return true;
  };

  // ✅ NEW: Handle View RAP Summary
  const handleViewSummary = () => {
    // TODO: Implement navigation to RAP Summary page
    setShowSummaryModal(true);
    //console.log('View RAP Summary for tower:', tower.id, 'type: CME');
    //toast.info('RAP Summary feature coming soon!');
  };

  // ============= RENDER HELPERS =============
  
  const getLevelColorClass = (level) => {
    switch (level) {
      case 0: return 'bg-gradient-to-r from-purple-600 to-purple-700';
      case 1: return 'bg-gradient-to-r from-blue-600 to-blue-700';
      case 2: return 'bg-gradient-to-r from-green-600 to-green-700';
      default: return 'bg-gradient-to-r from-gray-600 to-gray-700';
    }
  };
  
  const getLevelHoverClass = (level) => {
    switch (level) {
      case 0: return 'hover:bg-purple-500';
      case 1: return 'hover:bg-blue-500';
      case 2: return 'hover:bg-green-500';
      default: return 'hover:bg-gray-500';
    }
  };
  
  const getLevelButtonClass = (level) => {
    switch (level) {
      case 0: return 'bg-white text-purple-700 hover:bg-purple-50';
      case 1: return 'bg-white text-blue-700 hover:bg-blue-50';
      case 2: return 'bg-white text-green-700 hover:bg-green-50';
      default: return 'bg-white text-gray-700 hover:bg-gray-50';
    }
  };
  
  const getLevelBadgeClass = (level) => {
    switch (level) {
      case 0: return 'bg-purple-100 text-purple-800 border-purple-300';
      case 1: return 'bg-blue-100 text-blue-800 border-blue-300';
      case 2: return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  const formatVolume = (value) => {
    if (!value && value !== 0) return '0';
    return new Intl.NumberFormat('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };
  
  const renderItemRow = (categoryId, item, index) => {
    const isNotUsedStyle = item.isNotUsed ? 'opacity-50 bg-gray-100' : '';
    const isDisabled = viewMode === 'view';
    // Get TR CODE label for view mode
    const trCodeOption = TR_CODE_OPTIONS.find(opt => opt.value === item.trCode);
    const trCodeLabel = trCodeOption ? trCodeOption.label : '-';    

    return (
      <tr key={item.tempId} className={`border-b border-gray-200 hover:bg-gray-50 ${isNotUsedStyle}`}>
        <td className="px-4 py-3 text-center text-sm font-medium text-gray-900">
          {index + 1}
        </td>
        
        <td className="px-4 py-3 text-sm text-gray-900">
          <div className="flex items-start gap-2">
            <Package className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-600">
                  {item.itemCode || item.itemNumber}
                </span>
                {item.sourceType === 'NEW' && (
                  <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 font-semibold">
                    NEW
                  </span>
                )}
              </div>
              <div className="text-sm font-medium text-gray-900 mt-0.5">
                {item.itemDescription}
              </div>
            </div>
          </div>
        </td>
        
        <td className="px-4 py-3 text-center text-sm text-gray-900">
          {item.unit}
        </td>
        
        <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
          {formatVolume(item.volume)}
        </td>
        
        <td className="px-4 py-3 text-right text-sm text-gray-900">
          {item.boqUnitPrice ? formatCurrency(item.boqUnitPrice) : '-'}
        </td>
        
        <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
          {item.boqTotalPrice ? formatCurrency(item.boqTotalPrice) : '-'}
        </td>
        
        {/* NOT USED Checkbox - ✅ DISABLED IN VIEW MODE */}
        <td className="px-4 py-3 text-center">
          <input
            type="checkbox"
            checked={item.isNotUsed}
            onChange={(e) => handleCheckboxChange(categoryId, item.tempId, 'isNotUsed', e.target.checked)}
            className="w-4 h-4 text-red-600 rounded focus:ring-2 focus:ring-red-500"
            disabled={isDisabled || item.sourceType === 'NEW' || item.isOverride}
          />
        </td>
        
        {/* OVERRIDE Checkbox - ✅ DISABLED IN VIEW MODE */}
        <td className="px-4 py-3 text-center">
          <input
            type="checkbox"
            checked={item.isOverride}
            onChange={(e) => handleCheckboxChange(categoryId, item.tempId, 'isOverride', e.target.checked)}
            className="w-4 h-4 text-orange-600 rounded focus:ring-2 focus:ring-orange-500"
            disabled={isDisabled || item.sourceType === 'NEW' || item.isNotUsed}
          />
        </td>
        
        {/* RAP Unit Price - ✅ DISABLED IN VIEW MODE */}
        <td className="px-4 py-3 text-right text-sm font-medium text-blue-900">
          {item.isNotUsed ? (
            <span className="text-gray-400 italic">-</span>
          ) : item.isOverride ? (
            <div className="flex items-center justify-end gap-2">
              <input
                type="number"
                value={item.rapUnitPrice}
                onChange={(e) => handleInlineOverridePrice(categoryId, item.tempId, parseFloat(e.target.value) || 0)}
                className="w-32 px-2 py-1 text-right border border-orange-300 rounded focus:ring-2 focus:ring-orange-500"
                min="0"
                step="0.01"
                disabled={isDisabled}
              />
            </div>
          ) : (
            formatCurrency(item.rapUnitPrice)
          )}
        </td>
        
        {/* RAP Price */}
        <td className="px-4 py-3 text-right text-sm font-bold text-blue-600">
          {item.isNotUsed ? (
            <span className="text-gray-400 italic">-</span>
          ) : (
            formatCurrency(item.rapTotalPrice)
          )}
        </td>

        {/* ✅ NEW: TR CODE Column */}{/* TR CODE - ✅ CONDITIONAL: DROPDOWN IN EDIT, TEXT IN VIEW */}
        <td className="px-4 py-3">
          {viewMode === 'edit' ? (
            <select 
              value={item.trCode || ''} 
              onChange={(e) => handleTRCodeChange(categoryId, item.tempId, e.target.value || null)}
              className={`w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 ${
                !item.trCode ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
              }`}
              disabled={isDisabled}
            >
              <option value="">Pilih TR CODE</option>
              {TR_CODE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-gray-900">{trCodeLabel}</span>
          )}
        </td>
        
        {/* ✅ NEW: POSTING Column */}
        <td className="px-4 py-3">
          {viewMode === 'edit' ? (
            <select 
              value={item.postingType || ''} 
              onChange={(e) => handlePostingChange(categoryId, item.tempId, e.target.value || null)}
              className={`w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 ${
                !item.postingType ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
              }`}
              disabled={isDisabled}
            >
              <option value="">Pilih POSTING</option>
              {POSTING_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-gray-900">{item.postingType || '-'}</span>
          )}
        </td>
        
        {/* ACTIONS */}

        <td className="px-4 py-3">
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => handleEditItem(categoryId, item)}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Edit"
              disabled={isDisabled}
            >
              <Edit2 className="w-4 h-4" />
            </button>
            {item.canDelete && (
              <button
                onClick={() => handleDeleteItem(categoryId, item.tempId)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete"
                disabled={isDisabled}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };
  
  /**
   * ✅ Render category recursively (tree structure)
   */
  const renderCategory = (category, level = 0) => {
    const isExpanded = expandedCategories.has(category.id);
    const categoryData = rapData[category.id] || {};
    const items = categoryData.items || [];
    const hasChildren = category.children && category.children.length > 0;
    const isLeaf = category.canAddDirect && !hasChildren;
    
    // Calculate totals for leaf categories
    let boqSubtotal = 0;
    let rapSubtotal = 0;
    if (isLeaf && items.length > 0) {
      boqSubtotal = items.reduce((sum, item) => sum + (parseFloat(item.boqTotalPrice) || 0), 0);
      rapSubtotal = items.reduce((sum, item) => sum + (parseFloat(item.rapTotalPrice) || 0), 0);
    }
    
    // Indentation based on level
    const indentClass = level === 0 ? '' : level === 1 ? 'ml-8' : 'ml-16';
    
    return (
      <div key={category.id} className={`${indentClass} mb-4`}>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          {/* Category Header */}
          <div className={`${getLevelColorClass(level)} p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                {(hasChildren || isLeaf) && (
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className={`p-1 ${getLevelHoverClass(level)} rounded transition-colors`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-white" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-white" />
                    )}
                  </button>
                )}
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-white">
                      {category.categoryCode} - {category.categoryName}
                    </h3>
                    
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getLevelBadgeClass(level)}`}>
                      L{level}
                    </span>
                    
                    {category.isGroup && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded text-xs font-semibold">
                        Group
                      </span>
                    )}
                    {isLeaf && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-800 border border-green-300 rounded text-xs font-semibold">
                        Leaf
                      </span>
                    )}
                  </div>
                  
                  {category.subtitle && (
                    <p className="text-sm text-white opacity-90 mt-1">{category.subtitle}</p>
                  )}
                  
                  {isLeaf && (
                    <p className="text-sm text-white opacity-90 mt-1">
                      {items.length} items • BOQ: {formatCurrency(boqSubtotal)} • RAP: {formatCurrency(rapSubtotal)}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Add Item Button - ✅ ONLY SHOW IN EDIT MODE */}
              {viewMode === 'edit' && isLeaf && category.canAddDirect && (
                <button
                  onClick={() => handleAddItem(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 ${getLevelButtonClass(level)} rounded-lg transition-colors shadow-md`}
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              )}
            </div>
          </div>

          {isLeaf && isExpanded && (
            <div className="p-4">
              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No items in this category</p>
                  {category.canAddDirect && (
                    <p className="text-sm mt-1">Click "Add Item" to start</p>
                  )}
                  {!category.canAddDirect && (
                    <p className="text-sm mt-1 text-amber-600 italic">
                      This is a group category. Items should be added to subcategories.
                    </p>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-300 rounded-lg">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                        <th className="px-4 py-3 text-left text-sm font-bold" style={{ width: '60px' }}>NO</th>
                        <th className="px-4 py-3 text-left text-sm font-bold">DESCRIPTION</th>
                        <th className="px-4 py-3 text-center text-sm font-bold" style={{ width: '80px' }}>UNIT</th>
                        <th className="px-4 py-3 text-center text-sm font-bold" style={{ width: '100px' }}>VOL</th>
                        <th className="px-4 py-3 text-center text-sm font-bold" colSpan="2">BOQ/RAB</th>
                        <th className="px-4 py-3 text-center text-sm font-bold" style={{ width: '80px' }}>NOT USED</th>
                        <th className="px-4 py-3 text-center text-sm font-bold" style={{ width: '80px' }}>OVERRIDE</th>
                        <th className="px-4 py-3 text-center text-sm font-bold" colSpan="2">RAP</th>
                        {/* ✅ NEW: TR CODE column */}
                        <th className="px-4 py-3 text-center text-sm font-bold" style={{ width: '180px' }}>TR CODE</th>
                        {/* ✅ NEW: POSTING column */}
                        <th className="px-4 py-3 text-center text-sm font-bold" style={{ width: '120px' }}>POSTING</th>

                        <th className="px-4 py-3 text-center text-sm font-bold" style={{ width: '100px' }}>ACTIONS</th>
                      </tr>
                      <tr className="bg-blue-50 text-gray-700 text-xs">
                        <th className="px-4 py-2"></th>
                        <th className="px-4 py-2"></th>
                        <th className="px-4 py-2"></th>
                        <th className="px-4 py-2"></th>
                        <th className="px-4 py-2 text-center">Unit Price</th>
                        <th className="px-4 py-2 text-center">Price</th>
                        <th className="px-4 py-2"></th>
                        <th className="px-4 py-2"></th>
                        <th className="px-4 py-2 text-center">Unit Price</th>
                        <th className="px-4 py-2 text-center">Price</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {items.map((item, index) => renderItemRow(category.id, item, index))}
                    </tbody>
                    
                    <tfoot>
                      <tr className="bg-gray-50 font-bold">
                        <td colSpan="5" className="px-4 py-3 text-right text-sm">
                          BOQ Subtotal {category.categoryCode}:
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900">
                          {formatCurrency(boqSubtotal)}
                        </td>
                        <td colSpan="3" className="px-4 py-3 text-right text-sm">
                          RAP Subtotal {category.categoryCode}:
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-blue-600">
                          {formatCurrency(rapSubtotal)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                  
                  {/* Approve/Unapprove Buttons - ✅ ONLY SHOW IN EDIT MODE */}
                  {viewMode === 'edit' && categoryData.rapId && (
                    <div className="mt-4 flex justify-end gap-2">
                      {categoryData.isApproved ? (
                        <button
                          onClick={() => handleUnapprove(category.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          Unapprove RAP
                        </button>
                      ) : (
                        <button
                          onClick={() => handleApprove(category.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Approve RAP
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="mt-4">
            {category.children.map(child => renderCategory(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // ============= MAIN RENDER =============
  
  if (isLoadingCategories || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading CME RAP...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Tower Site Budgeting CME - RAP</h1>
              <p className="text-gray-600 mt-1">{tower.siteName} ({tower.siteId})</p>
              <p className="text-sm text-gray-500 mt-1">
                Province: {tower.provinsi} • Coefficient: ×{priceCoefficient.toFixed(4)}
                {/* ✅ NEW: Show mode indicator */}
                {viewMode === 'view' && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                    VIEW MODE
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* ✅ NEW: Action Buttons Container */}
          <div className="flex items-center gap-3">

            {/* Export Detail Buttons - Only in edit mode */}
            {viewMode === 'edit' && (
              <div className="flex items-center gap-2 border-r border-gray-300 pr-3">
                <button
                  onClick={handleExportDetailPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md"
                  title="Export RAP Detail to PDF"
                >
                  <FileDown className="w-4 h-4" />
                  PDF
                </button>
                
                <button
                  onClick={handleExportDetailExcel}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md"
                  title="Export RAP Detail to Excel"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </button>
              </div>
            )}

            {/* ✅ NEW: View RAP Summary Button - Always visible */}
            <button
              onClick={handleViewSummary}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg"
            >
              <FileText className="w-5 h-5" />
              View RAP Summary
            </button>
            
            {/* Save RAP Button - Only in edit mode */}
            {viewMode === 'edit' && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                {isSaving ? 'Saving...' : 'Save RAP'}
              </button>
            )}
          </div>

        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-1">CME RAP - Hierarchical Structure</h3>
            <p className="text-sm text-blue-800 mb-2">
              Review BOQ prices and set budget overrides where needed. Use checkboxes to mark items as "Not Used" or enable price "Override".
            </p>
            <div className="text-sm text-blue-800">
              <strong>Color Coding:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><span className="font-semibold text-purple-700">Purple</span> - Level 0 (Root categories: A-J)</li>
                <li><span className="font-semibold text-blue-700">Blue</span> - Level 1 (Sub-categories: A.1, A.2, A.3)</li>
                <li><span className="font-semibold text-green-700">Green</span> - Level 2 (Leaf categories: A.2.a-g)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {categories.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
          <p className="text-yellow-800 font-medium">No CME categories available</p>
          <p className="text-sm text-yellow-700 mt-1">Please contact administrator to set up budget categories</p>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map(category => renderCategory(category, 0))}
        </div>
      )}

      {/* Item Picker Modal - ✅ ONLY AVAILABLE IN EDIT MODE */}
      {viewMode === 'edit' && (
      <ItemPickerModal
        isOpen={showItemPicker}
        onSelect={handleItemSelect}
        onClose={() => setShowItemPicker(false)}
      />
      )}
      
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Edit Item</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Volume - ✅ DISABLED IN VIEW MODE */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Volume</label>
                <input
                  type="number"
                  value={editFormData.volume}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, volume: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                  disabled={viewMode === 'view'}
                />
              </div>
              
              {/* Not Used Checkbox - ✅ DISABLED IN VIEW MODE */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-not-used"
                  checked={editFormData.isNotUsed}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, isNotUsed: e.target.checked, isOverride: false }))}
                  className="w-4 h-4 text-red-600 rounded"
                  disabled={viewMode === 'view'}
                />
                <label htmlFor="edit-not-used" className="text-sm text-gray-700">Not Used</label>
              </div>
              
              {/* Override Checkbox - ✅ DISABLED IN VIEW MODE */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-override"
                  checked={editFormData.isOverride}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, isOverride: e.target.checked, isNotUsed: false }))}
                  className="w-4 h-4 text-orange-600 rounded"
                  disabled={viewMode === 'view' || editFormData.isNotUsed}
                />
                <label htmlFor="edit-override" className="text-sm text-gray-700">Override Price</label>
              </div>
              
              {/* Override Price Input - ✅ DISABLED IN VIEW MODE */}
              {editFormData.isOverride && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Override Unit Price</label>
                  <input
                    type="number"
                    value={editFormData.overriddenRapUnitPrice}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, overriddenRapUnitPrice: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    min="0"
                    step="0.01"
                    disabled={viewMode === 'view'}
                  />
                </div>
              )}
              
              {/* Remarks - ✅ DISABLED IN VIEW MODE */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={editFormData.remarks}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  disabled={viewMode === 'view'}
                />
              </div>
            </div>

            {/* ✅ NEW: TR CODE ✅ DISABLED IN VIEW MODE */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TR CODE</label>
              <select
                value={editFormData.trCode || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, trCode: e.target.value || null }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={viewMode === 'view'}
              >
                <option value="">Pilih TR CODE</option>
                {TR_CODE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* ✅ NEW: POSTING TYPE - ✅ DISABLED IN VIEW MODE */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">POSTING</label>
              <select
                value={editFormData.postingType || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, postingType: e.target.value || null }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={viewMode === 'view'}
              >
                <option value="">Pilih POSTING</option>
                {POSTING_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>   

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {viewMode === 'view' ? 'Close' : 'Cancel'}
              </button>
              {viewMode === 'edit' && (
              <button
                onClick={handleSaveEdit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      <TowerBudgetingCMESummaryModal
        isOpen={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        tower={tower}
        rapData={rapData}
        categories={categories}
        rapType="CME"
      />

    </div>
  );
};

export default TowerBudgetingCMEForm;